// ═══════════════════════════════════════════════════════════════
// ClearView CRM Permission Defaults — Role Templates & Module Definitions
// Location: src/constants/permissionDefaults.js
// ═══════════════════════════════════════════════════════════════

// ── Module definitions (12 modules in 5 groups) ────────────────

export const MODULE_DEFINITIONS = [
  // Member Management
  {
    key: "members",
    label: "View Donors",
    group: "Donor Management",
    description: "See the donor list and search",
  },
  {
    key: "addMembers",
    label: "Add / Import Donors",
    group: "Donor Management",
    description: "Create new donors or import from CSV",
  },
  {
    key: "memberDetail",
    label: "View Donor Detail",
    group: "Donor Management",
    description: "Open individual donor records",
  },
  {
    key: "editMembers",
    label: "Edit Donor Records",
    group: "Donor Management",
    description: "Change donor data, flags, classifications",
  },
  // Financial
  {
    key: "gifts",
    label: "Gifts & Receipting",
    group: "Financial",
    description: "Record gifts, generate receipts, view donor financial data",
  },
  // Engagement
  {
    key: "cultivation",
    label: "Cultivation View",
    group: "Engagement",
    description: "Pipeline, actions, overview, and cultivation intelligence",
  },
  {
    key: "events",
    label: "Events & Attendance",
    group: "Engagement",
    description: "Create events, take attendance, add prospects",
  },
  {
    key: "logContacts",
    label: "Log Contacts",
    group: "Engagement",
    description: "Record contact log entries on donor records",
  },
  {
    key: "tasks",
    label: "Director Tasks",
    group: "Engagement",
    description: "View and manage director task lists",
  },
  {
    key: "contactReports",
    label: "Donor Contact Reports",
    group: "Engagement",
    description: "File and view comprehensive donor contact reports",
  },
  // Intelligence
  {
    key: "reports",
    label: "Reports & Analytics",
    group: "Intelligence",
    description: "Dashboard, pathways, portfolios, roster, types",
  },
  {
    key: "reportsDonors",
    label: "Reports — Donors Tab",
    group: "Intelligence",
    description: "Donor names and flags (no dollar amounts for restricted tiers)",
  },
  {
    key: "portfolios",
    label: "Director Portfolios",
    group: "Intelligence",
    description: "View and drill into director portfolio assignments",
  },
  // Administration
  {
    key: "settings",
    label: "Settings",
    group: "Administration",
    description:
      "Appearance, fields, scoring, chapter, data, users, permissions",
  },
];

// ── Unique group names in display order ────────────────────────

export const MODULE_GROUPS = [
  "Donor Management",
  "Financial",
  "Engagement",
  "Intelligence",
  "Administration",
];

// ── All permission keys (for iteration) ────────────────────────

export const ALL_PERMISSION_KEYS = MODULE_DEFINITIONS.map((m) => m.key);

// ── Full permission set (all true) ─────────────────────────────

const ALL_TRUE = {};
ALL_PERMISSION_KEYS.forEach((k) => {
  ALL_TRUE[k] = true;
});

const ALL_FALSE = {};
ALL_PERMISSION_KEYS.forEach((k) => {
  ALL_FALSE[k] = false;
});

// ── Role templates ─────────────────────────────────────────────

export const ROLE_TEMPLATES = [
  {
    id: "admin",
    label: "Admin",
    dbTier: "admin",
    color: "#991b1b",
    description: "Full access — cannot be restricted",
    locked: true,
    permissions: { ...ALL_TRUE },
  },
  {
    id: "director",
    label: "Director",
    dbTier: "director",
    color: "#7c3aed",
    description: "Manages donor portfolios, cultivation, and engagement — full access minus Settings",
    locked: false,
    permissions: {
      ...ALL_TRUE,
      settings: false,
    },
  },
  {
    id: "event_manager",
    label: "Event Manager",
    dbTier: "volunteer",
    color: "#0d9488",
    description: "Events, attendance, prospect entry, and contact logging",
    locked: false,
    permissions: {
      members: true,
      addMembers: false,
      memberDetail: false,
      editMembers: false,
      gifts: false,
      cultivation: false,
      events: true,
      logContacts: true,
      tasks: false,
      contactReports: false,
      reports: false,
      reportsDonors: false,
      portfolios: false,
      settings: false,
    },
  },
  {
    id: "gift_processor",
    label: "Gift Processor",
    dbTier: "director",
    color: "#b45309",
    description: "Gift entry, receipting, and donor record management",
    locked: false,
    permissions: {
      members: true,
      addMembers: false,
      memberDetail: true,
      editMembers: true,
      gifts: true,
      cultivation: false,
      events: false,
      logContacts: false,
      tasks: false,
      contactReports: false,
      reports: false,
      reportsDonors: false,
      portfolios: false,
      settings: false,
    },
  },
  {
    id: "data_analyst",
    label: "Data Analyst",
    dbTier: "director",
    color: "#2563eb",
    description: "Reports, portfolios, and read-only donor and cultivation access",
    locked: false,
    permissions: {
      members: true,
      addMembers: false,
      memberDetail: true,
      editMembers: false,
      gifts: false,
      cultivation: true,
      events: false,
      logContacts: false,
      tasks: false,
      contactReports: false,
      reports: true,
      reportsDonors: true,
      portfolios: true,
      settings: false,
    },
  },
];

// ── Helpers ────────────────────────────────────────────────────

export const getTemplate = (id) => ROLE_TEMPLATES.find((t) => t.id === id);

export const getDbTier = (roleId) => getTemplate(roleId)?.dbTier || "volunteer";

// Build default rolePermissions object from templates (excludes admin)
export const buildDefaultPermissions = () => {
  const defaults = {};
  ROLE_TEMPLATES.forEach((t) => {
    if (!t.locked) {
      defaults[t.id] = {
        template: t.id,
        customized: false,
        permissions: { ...t.permissions },
      };
    }
  });
  return defaults;
};

// Build ROLE_STYLES from templates (for UsersPanel, badges, etc.)
export const ROLE_STYLES = {};
ROLE_TEMPLATES.forEach((t) => {
  ROLE_STYLES[t.id] = {
    label: t.label,
    color: t.color,
    bg: t.color + "18",
    border: t.color + "40",
  };
});

// ── Dependency warnings ────────────────────────────────────────
// Returns array of warning strings for a given permission set

export const getPermissionWarnings = (perms) => {
  const warnings = [];
  if (perms.addMembers && !perms.members)
    warnings.push(
      "'Add Donors' is on but 'View Donors' is off — users can't reach the Add button."
    );
  if (perms.editMembers && !perms.memberDetail)
    warnings.push(
      "'Edit Donors' is on but 'View Donor Detail' is off — users can't reach the edit screen."
    );
  if (perms.logContacts && !perms.memberDetail)
    warnings.push(
      "'Log Contacts' is on but 'View Donor Detail' is off — contacts are logged from the detail view."
    );
  if (perms.gifts && !perms.memberDetail)
    warnings.push(
      "'Gifts' is on but 'View Donor Detail' is off — gifts are accessed from the detail view."
    );
  if (perms.reportsDonors && !perms.reports)
    warnings.push(
      "'Donors Tab' is on but 'Reports' is off — the Donors tab is inside Reports."
    );
  return warnings;
};
